<template>
  <div class="event-scroll-list">
    <div class="section-header">
      <h3>{{ title }}</h3>
      <span v-if="!normal" class="count" :class="{ zero: records.length === 0 }">{{ records.length }}</span>
      <div v-if="normal" class="inspect-summary">
        <span class="summary-item">
          <i class="summary-dot"></i>{{ summary.totalLabel }}：{{ normal.total }}{{ summary.unit }}
        </span>
        <span class="summary-divider"></span>
        <span class="summary-item">{{ summary.alarmLabel }}：{{ normal.alarm_count || 0 }}</span>
      </div>
    </div>

    <template v-if="normal">
      <div v-if="normal.devices && normal.devices.length" ref="scrollContainer" class="scroll-container">
        <div :class="normal.kind === 'pot' ? 'pot-list' : 'camera-list'" :style="normal.kind === 'pot' ? potGridStyle : null">
          <template v-if="normal.kind === 'pot'">
            <div v-for="(device, index) in loopDevices" :key="'pot-' + index" class="pot-row">
              <div class="pot-image">
                <img v-if="device.image" :src="device.image" :alt="device.name" />
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 10h16v5a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-5z"></path>
                  <path d="M6 10V8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2"></path>
                  <path d="M2 12h2M20 12h2"></path>
                </svg>
              </div>
              <div class="pot-meta">
                <span class="pot-name">{{ device.name }}</span>
                <span class="pot-status">正常</span>
              </div>
              <span class="pot-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="9"></circle>
                  <path d="m8.5 12 2.5 2.5 4.5-5"></path>
                </svg>
              </span>
            </div>
          </template>
          <template v-else>
            <div v-for="(device, index) in loopDevices" :key="'camera-' + index" class="camera-row">
              <span class="camera-dot"></span>
              <span class="camera-thumb">
                <img v-if="device.image" :src="device.image" :alt="device.name" />
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="6" width="13" height="12" rx="2"></rect>
                  <path d="m16 10 5-3v10l-5-3"></path>
                </svg>
              </span>
              <span class="camera-time">{{ normal.time || '--:--:--' }}</span>
              <span class="camera-name">{{ device.name }}</span>
              <span class="camera-ok">巡检正常</span>
            </div>
          </template>
        </div>
      </div>
      <div v-else class="normal-empty">
        暂未配置{{ normal.kind === 'pot' ? '锅位' : '摄像头' }}
      </div>
    </template>

    <div v-else-if="records.length === 0" class="empty">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
             stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          <path d="m9 12 2 2 4-4"></path>
        </svg>
      </div>
      <div class="empty-title">近24小时暂无异常</div>
      <div class="empty-desc">设备运行正常，未发现告警事件</div>
    </div>
    <div v-else ref="scrollContainer" class="scroll-container">
      <div class="event-list">
        <div v-for="(item, index) in loopRecords" :key="index"
             :class="['event-item', 'level-' + (item.level || '').toLowerCase()]">
          <div class="event-info">
            <div class="event-name">{{ item.name }}</div>
            <div class="event-time">{{ item.time }}</div>
          </div>
          <div class="event-image">
            <img v-if="item.image" :src="item.image" class="image" />
            <div v-else class="no-image">无图片</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EventScrollList',
  props: {
    title: {
      type: String,
      default: ''
    },
    records: {
      type: Array,
      default: () => []
    },
    normal: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      timer: null,
      potColumns: 2,
      resizeObserver: null
    }
  },
  computed: {
    // 复制一份用于无缝滚动
    loopRecords() {
      return this.records.length ? [...this.records, ...this.records] : []
    },
    loopDevices() {
      const devices = (this.normal && this.normal.devices) || []
      return devices.length ? [...devices, ...devices] : []
    },
    summary() {
      if (this.normal && this.normal.kind === 'pot') {
        return { totalLabel: '监测锅位总数', unit: '个', alarmLabel: '当前告警' }
      }
      return { totalLabel: '摄像头总数', unit: '路', alarmLabel: '今日告警' }
    },
    potGridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.potColumns}, minmax(0, 1fr))`
      }
    }
  },
  watch: {
    records() {
      if (this.normal) return
      this.$nextTick(() => this.startAutoScroll())
    },
    normal() {
      this.$nextTick(() => this.startAutoScroll())
    },
    potColumns() {
      this.$nextTick(() => this.startAutoScroll())
    }
  },
  mounted() {
    this.updatePotColumns()
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.updatePotColumns)
      this.resizeObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.updatePotColumns)
    }
    this.$nextTick(() => this.startAutoScroll())
  },
  beforeDestroy() {
    this.stopScroll()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updatePotColumns)
    }
  },
  methods: {
    updatePotColumns() {
      const width = this.$el ? this.$el.clientWidth : 0
      // 锅位卡片至少需要 180px 才能放下图片、名称和状态，最多两列
      const minCardWidth = 180
      const gap = 8
      const padding = 32
      const fit = Math.floor((width - padding + gap) / (minCardWidth + gap))
      this.potColumns = Math.max(1, Math.min(2, fit || 1))
    },
    startAutoScroll() {
      this.startScroll()
    },
    startScroll() {
      this.stopScroll()
      const container = this.$refs.scrollContainer
      if (!container) return

      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight
      if (scrollHeight <= clientHeight) return

      const halfHeight = scrollHeight / 2
      let scrollTop = 0
      container.scrollTop = 0

      this.timer = setInterval(() => {
        scrollTop += 1
        if (scrollTop >= halfHeight) {
          container.scrollTop = 0
          scrollTop = 0
        } else {
          container.scrollTop = scrollTop
        }
      }, 30)
    },
    stopScroll() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    }
  }
}
</script>

<style scoped>
.event-scroll-list {
  height: 100%;
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
  min-width: 0;
}

h3 {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.count {
  background: #fc8181;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: auto;
}

.count.zero {
  background: rgba(72, 187, 120, 0.18);
  color: #68d391;
}

.inspect-summary {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(49, 130, 206, 0.1);
  border: 1px solid rgba(99, 179, 237, 0.16);
  font-size: 12px;
  color: #cbd5e0;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.summary-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #48bb78;
  box-shadow: 0 0 8px rgba(72, 187, 120, 0.9);
}

.summary-divider {
  width: 1px;
  height: 12px;
  background: rgba(160, 174, 192, 0.3);
}

.camera-list {
  position: relative;
}

.camera-list::before {
  content: "";
  position: absolute;
  left: 4px;
  top: 14px;
  bottom: 14px;
  width: 1px;
  background: linear-gradient(180deg,
    rgba(72, 187, 120, 0.08),
    rgba(72, 187, 120, 0.45),
    rgba(72, 187, 120, 0.08));
}

.camera-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px 8px 20px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.08);
}

.camera-dot {
  position: absolute;
  left: 1px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #48bb78;
  box-shadow: 0 0 8px rgba(72, 187, 120, 0.9);
}

.camera-thumb {
  flex-shrink: 0;
  width: 42px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(99, 179, 237, 0.18);
  color: #cbd5e0;
}

.camera-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.camera-thumb svg {
  width: 22px;
  height: 22px;
}

.camera-time {
  flex-shrink: 0;
  min-width: 62px;
  font-family: "Bahnschrift", "DIN Alternate", monospace;
  font-size: 13px;
  color: #e2e8f0;
}

.camera-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.camera-ok {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: #68d391;
  white-space: nowrap;
}

.pot-list {
  display: grid;
  gap: 8px;
}

.pot-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid rgba(99, 179, 237, 0.22);
  background: linear-gradient(90deg, rgba(45, 90, 145, 0.45), rgba(20, 50, 90, 0.45));
}

.pot-image {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.16);
  overflow: hidden;
  color: #a0aec0;
}

.pot-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.pot-image svg {
  width: 34px;
  height: 34px;
}

.pot-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pot-name {
  min-width: 0;
  font-size: 14px;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pot-status {
  font-size: 12px;
  font-weight: 600;
  color: #68d391;
}

.pot-check {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #48bb78;
}

.pot-check svg {
  width: 22px;
  height: 22px;
}

.normal-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #718096;
  font-size: 13px;
}

.empty {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  overflow: hidden;
}

.empty::before,
.empty::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}

.empty::before {
  width: 180px;
  height: 180px;
  background: radial-gradient(circle, rgba(72, 187, 120, 0.1), transparent 70%);
}

.empty::after {
  width: 240px;
  height: 240px;
  border: 1px dashed rgba(72, 187, 120, 0.12);
}

.empty-icon {
  position: relative;
  z-index: 1;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle, rgba(72, 187, 120, 0.25), rgba(72, 187, 120, 0.04));
  border: 1px solid rgba(72, 187, 120, 0.45);
  box-shadow: 0 0 24px rgba(72, 187, 120, 0.18);
}

.empty-icon::after {
  content: "";
  position: absolute;
  inset: -1px;
  border-radius: 50%;
  border: 1px solid rgba(72, 187, 120, 0.4);
  animation: empty-pulse 2.4s ease-out infinite;
}

.empty-icon svg {
  width: 30px;
  height: 30px;
  color: #68d391;
}

.empty-title {
  position: relative;
  z-index: 1;
  font-size: 15px;
  font-weight: 600;
  color: #e2e8f0;
}

.empty-desc {
  position: relative;
  z-index: 1;
  font-size: 12px;
  color: #718096;
}

@keyframes empty-pulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  70% {
    transform: scale(1.4);
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.scroll-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  height: 0;
}

.scroll-container::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.event-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0 6px 8px;
  margin-bottom: 8px;
  background: rgba(49, 130, 206, 0.1);
  border-radius: 8px;
  border-left: 3px solid #a0aec0;
}

/* 等级颜色参考 supplier_uniapp */
.event-item.level-critical {
  border-left-color: #f5222d;
}

.event-item.level-alarm {
  border-left-color: #fa8c16;
}

.event-item.level-warning {
  border-left-color: #faad14;
}

.event-image {
  width: 120px;
  height: 76px;
  flex-shrink: 0;
  margin-left: 10px;
  border-radius: 6px 0 0 6px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #718096;
  font-size: 12px;
}

.event-info {
  flex: 1;
  min-width: 0;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.event-name {
  font-size: 13px;
  color: #e2e8f0;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-time {
  font-size: 12px;
  color: #a0aec0;
}
</style>
